import React, { useEffect, useRef, useState } from 'react';
import { Modal, App } from 'antd';
import { useUpdateOrderGuestsMutation } from '../../../services/ordersApi';
import GuestCounters from '../GuestCounters';
import '../tables.css';

interface EditGuestsModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  tableId: string;
  tableNumber: string;
  adults: number;
  children: number;
  /** Capacidad de la mesa (o del grupo de mesas unidas). */
  capacity: number;
}

/**
 * Corrige los comensales de una mesa abierta: llegó más gente, se fue alguien
 * o nadie movió el contador al abrir. Lo que se guarda aquí es lo que cuentan
 * el corte de caja y Reportes.
 */
const EditGuestsModal: React.FC<EditGuestsModalProps> = ({
  open,
  onClose,
  orderId,
  tableId,
  tableNumber,
  adults: currentAdults,
  children: currentChildren,
  capacity,
}) => {
  const { message } = App.useApp();
  const [adults, setAdults] = useState(currentAdults);
  const [children, setChildren] = useState(currentChildren);
  const [updateGuests, { isLoading }] = useUpdateOrderGuestsMutation();

  // destroyOnClose no desmonta el modal: al reabrir hay que partir de lo
  // guardado, no de lo que quedó en los contadores la vez anterior. Solo al
  // abrir: si la orden se refresca con el modal abierto (otro dispositivo,
  // SignalR), no se le pisa al mesero lo que está capturando.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setAdults(currentAdults);
      setChildren(currentChildren);
    }
    wasOpen.current = open;
  }, [open, currentAdults, currentChildren]);

  const unchanged = adults === currentAdults && children === currentChildren;

  const handleSave = async () => {
    try {
      await updateGuests({ orderId, tableId, adults, children }).unwrap();
      message.success('Comensales actualizados');
      onClose();
    } catch (error: any) {
      message.error(error?.data?.message || 'No se pudieron actualizar los comensales');
    }
  };

  return (
    <Modal
      title={`Comensales · Mesa ${tableNumber}`}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={isLoading}
      okButtonProps={{ disabled: unchanged, size: 'large' }}
      cancelButtonProps={{ size: 'large' }}
      width={460}
      wrapClassName="open-table-modal"
      style={{ top: 30 }}
    >
      <GuestCounters
        adults={adults}
        children={children}
        onAdultsChange={setAdults}
        onChildrenChange={setChildren}
        capacity={capacity}
      />
    </Modal>
  );
};

export default EditGuestsModal;
